import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web--source/common/media";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled, { css } from "styled-components";
var MAP_AUS_WIDTH = 115;
var MAP_AUS_HEIGHT = 118;
var MAP_BASE_WIDTH = 1026;
var MAP_BASE_HEIGHT = 587;
var MAP_EU_WIDTH = 142;
var MAP_EU_HEIGHT = 169;
var MAP_NA_WIDTH = 328;
var MAP_NA_HEIGHT = 290;
export var GeographicMapDesktop = function() {
    var responsivelyRatio = useResponsiveValue({
        mobileAndAbove: 0,
        tabletAndAbove: MIN_WIDTH_BREAKPOINTS.TABLET / MIN_WIDTH_BREAKPOINTS.XLARGE,
        desktopAndAbove: MIN_WIDTH_BREAKPOINTS.DESKTOP / MIN_WIDTH_BREAKPOINTS.XLARGE,
        xLargeAndAbove: 1
    });
    var imageWidth = Math.round(MAP_BASE_WIDTH * responsivelyRatio);
    var imageHeight = Math.round(MAP_BASE_HEIGHT * responsivelyRatio);
    var naWidth = Math.round(MAP_NA_WIDTH * responsivelyRatio);
    var naHeight = Math.round(MAP_NA_HEIGHT * responsivelyRatio);
    var euWidth = Math.round(MAP_EU_WIDTH * responsivelyRatio);
    var euHeight = Math.round(MAP_EU_HEIGHT * responsivelyRatio);
    var ausWidth = Math.round(MAP_AUS_WIDTH * responsivelyRatio);
    var ausHeight = Math.round(MAP_AUS_HEIGHT * responsivelyRatio);
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsxs(Wrapper, {
        justify: "center",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "displayLSerifRegular",
                align: "center",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "oLZIWB",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Get more sales at home and abroad"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "5x"
            }),
            /*#__PURE__*/ _jsxs(MapWrapper, {
                children: [
                    /*#__PURE__*/ _jsx(BaseWrapper, {
                        children: /*#__PURE__*/ _jsx("img", {
                            src: optimizeImageUrl({
                                url: "https://cdn.faire.com/fastly/d892a95641090bd33c9c7de850b7b69cb0f2e93cb4286abbef40ee778da5e773.png"
                            }, isSSR, {
                                width: imageWidth,
                                height: imageHeight
                            }),
                            style: {
                                width: "".concat(imageWidth, "px"),
                                height: "".concat(imageHeight, "px")
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(ContinentWrapper, {
                        "data-test-id": "map-na",
                        $top: Math.round(5 * responsivelyRatio),
                        $left: 4,
                        $width: naWidth,
                        $height: naHeight,
                        children: /*#__PURE__*/ _jsx("img", {
                            src: optimizeImageUrl({
                                url: "https://cdn.faire.com/fastly/bd4035095bd157079050af6b8b652b86100d96d4ab5beaaf4e31da001feba74e.png"
                            }, isSSR, {
                                width: naWidth,
                                height: naHeight
                            }),
                            style: {
                                width: "".concat(naWidth, "px"),
                                height: "".concat(naHeight, "px")
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(ContinentWrapper, {
                        "data-test-id": "map-eu",
                        $top: Math.round(89 * responsivelyRatio),
                        $left: Math.round(453 * responsivelyRatio),
                        $width: euWidth,
                        $height: euHeight,
                        children: /*#__PURE__*/ _jsx("img", {
                            src: optimizeImageUrl({
                                url: "https://cdn.faire.com/fastly/cb0233c90bc7b290426e078b697cd2629c7c9ab77926677c6d9a183591a1bf54.png"
                            }, isSSR, {
                                width: euWidth,
                                height: euHeight
                            }),
                            style: {
                                width: "".concat(euWidth, "px"),
                                height: "".concat(euHeight, "px")
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(ContinentWrapper, {
                        "data-test-id": "map-aus",
                        $top: Math.round(417 * responsivelyRatio),
                        $left: Math.round(799 * responsivelyRatio),
                        $width: ausWidth,
                        $height: ausHeight,
                        children: /*#__PURE__*/ _jsx("img", {
                            src: optimizeImageUrl({
                                url: "https://cdn.faire.com/fastly/64050e66f041105ef61b351e29199f5ab39ff6a58443fffa46b6305485ab2a93.png"
                            }, isSSR, {
                                width: ausWidth,
                                height: ausHeight
                            }),
                            style: {
                                width: "".concat(ausWidth, "px"),
                                height: "".concat(ausHeight, "px")
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(TagWrapper, {
                justify: "center",
                children: /*#__PURE__*/ _jsxs(ContinentInfoCardWrapper, {
                    align: "center",
                    $isHighlighted: true,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "subheadingSansMedium",
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "PLO0pz",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "New markets"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "displayMSerifRegular",
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "BCuW//",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Coming soon"
                                    }
                                ]
                            })
                        })
                    ]
                })
            })
        ]
    });
};
var Wrapper = styled(Column).withConfig({
    componentId: "sc-132194ca-0"
})([
    "width:100%;position:relative;"
]);
var MapWrapper = styled.div.withConfig({
    componentId: "sc-132194ca-1"
})([
    "position:relative;margin:auto;width:",
    "px;height:747px;",
    "{width:0px;}",
    "{width:550px;height:480px;}",
    "{width:730px;height:580px;}"
], MAP_BASE_WIDTH, responsiveMediaQueries.mobile, responsiveMediaQueries.tablet, responsiveMediaQueries.desktop);
var BaseWrapper = styled.div.withConfig({
    componentId: "sc-132194ca-2"
})([
    "position:absolute;top:0;left:0;z-index:1;"
]);
var ContinentWrapper = styled.div.withConfig({
    componentId: "sc-132194ca-3"
})([
    "width:",
    "px;height:",
    "px;position:absolute;left:",
    ";top:",
    ";z-index:2;filter:invert(50%) sepia(10%) saturate(666%) hue-rotate(6deg) brightness(96%) contrast(90%);"
], function(props) {
    return props.$width;
}, function(props) {
    return props.$height;
}, function(param) {
    var $left = param.$left;
    return css([
        "",
        "px"
    ], $left);
}, function(param) {
    var $top = param.$top;
    return css([
        "",
        "px"
    ], $top);
});
var TagWrapper = styled(Row).withConfig({
    componentId: "sc-132194ca-4"
})([
    "width:100%;height:150px;position:absolute;z-index:3;bottom:0;"
]);
var ContinentInfoCardWrapper = styled(Column).withConfig({
    componentId: "sc-132194ca-5"
})([
    "padding:24px;height:150px;width:438px;background-color:",
    ";opacity:",
    ";border-radius:10px;box-sizing:border-box;box-shadow:0px 0px 4px rgba(0,0,0,0.25);border:1px solid ",
    ";"
], Core.surface.inverse, function(param) {
    var isHighlighted = param.$isHighlighted;
    return isHighlighted ? 1 : 0.5;
}, Core.border.muted);
